import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBankAccounts, addBankAccount, setPrimaryAccount, deleteBankAccount } from '../utils/api';

export default function BankAccountsPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Bank Accounts';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddBank, setShowAddBank]   = useState(false);
  const [bankLoading, setBankLoading]   = useState(false);
  const [msg, setMsg]                   = useState('');
  const [bankForm, setBankForm]         = useState({
    routingNumber: '', accountNumber: '', accountType: 'checking', bankName: ''
  });

  useEffect(() => {
    getBankAccounts().then(r => setBankAccounts(r.data)).catch(console.error);
  }, []);

  async function handleAddBank() {
    if (!bankForm.routingNumber || !bankForm.accountNumber) {
      setMsg('Routing and account number required');
      return;
    }
    setBankLoading(true); setMsg('');
    try {
      await addBankAccount(bankForm);
      const res = await getBankAccounts();
      setBankAccounts(res.data);
      setShowAddBank(false);
      setBankForm({ routingNumber: '', accountNumber: '', accountType: 'checking', bankName: '' });
      setMsg('Bank account added successfully');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to add bank account');
    } finally { setBankLoading(false); }
  }

  async function handleSetPrimary(id) {
    try {
      await setPrimaryAccount(id);
      const res = await getBankAccounts();
      setBankAccounts(res.data);
      setMsg('Primary account updated');
    } catch (err) { setMsg('Failed to set primary'); }
  }

  async function handleDeleteBank(id) {
    try {
      await deleteBankAccount(id);
      const res = await getBankAccounts();
      setBankAccounts(res.data);
      setMsg('Account removed');
    } catch (err) { setMsg('Failed to remove account'); }
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Bank Accounts</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        <div style={s.subtitle}>
          Your bank accounts are used for cashouts. The primary account is used for auto cashout.
        </div>

        {/* Account list */}
        {bankAccounts.length > 0 && (
          <div style={s.card}>
            {bankAccounts.map((account, i) => (
              <div key={account.id} style={{...s.bankRow, borderBottom: i < bankAccounts.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                <div style={s.bankIconWrap}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
                </div>
                <div style={s.bankInfo}>
                  <div style={s.bankName}>
                    {account.bankName || 'Bank'} •••{account.last4}
                  </div>
                  <div style={s.bankMeta}>
                    <span style={s.bankType}>{account.accountType}</span>
                    {account.isPrimary && <span style={s.primaryBadge}>Primary</span>}
                  </div>
                </div>
                <div style={s.bankActions}>
                  {!account.isPrimary && (
                    <button style={s.primaryBtn} onClick={() => handleSetPrimary(account.id)}>
                      Set primary
                    </button>
                  )}
                  <button style={s.deleteBtn} onClick={() => handleDeleteBank(account.id)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {bankAccounts.length === 0 && !showAddBank && (
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
          </div>
            <div style={s.emptyTitle}>No bank accounts yet</div>
            <div style={s.emptySub}>Add a bank account to enable cashouts</div>
          </div>
        )}

        {msg && (
          <div style={{...s.msg, color: msg.includes('Failed') || msg.includes('required') ? '#FF3B30' : '#00C48C'}}>
            {msg}
          </div>
        )}

        {/* Add bank form */}
        {showAddBank ? (
          <div style={s.formCard}>
            <div style={s.formTitle}>Add bank account</div>

            <select style={s.input}
              value={bankForm.accountType}
              onChange={e => setBankForm({...bankForm, accountType: e.target.value})}>
              <option value="checking">Checking account</option>
              <option value="savings">Savings account</option>
            </select>

            <input style={s.input}
              placeholder="Bank name (e.g. Chase, Wells Fargo)"
              value={bankForm.bankName}
              onChange={e => setBankForm({...bankForm, bankName: e.target.value})}/>

            <input style={s.input}
              placeholder="Routing number (9 digits)"
              value={bankForm.routingNumber}
              maxLength={9}
              type="number"
              onChange={e => setBankForm({...bankForm, routingNumber: e.target.value})}/>

            <input style={s.input}
              placeholder="Account number"
              value={bankForm.accountNumber}
              type="number"
              onChange={e => setBankForm({...bankForm, accountNumber: e.target.value})}/>

            <div style={s.secureNote}>
              🔒 Your bank details are encrypted and stored securely via Stripe.
            </div>

            <div style={s.formBtns}>
              <button style={s.cancelBtn} onClick={() => { setShowAddBank(false); setMsg(''); }}>
                Cancel
              </button>
              <button style={s.addBtn} onClick={handleAddBank} disabled={bankLoading}>
                {bankLoading ? 'Adding...' : 'Add account'}
              </button>
            </div>
          </div>
        ) : (
          <button style={s.addBankBtn} onClick={() => setShowAddBank(true)}>
            + Add bank account
          </button>
        )}

        {/* Info section */}
        <div style={s.infoCard}>
          <div style={s.infoTitle}>How it works</div>
          <div style={s.infoItem}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0, marginRight:8}}><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 12h.01M18 12h.01"/></svg>
            Cash out your Spend balance to any connected account
          </div>
          <div style={s.infoItem}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0, marginRight:8}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Primary account is used for auto cashout
          </div>
          <div style={s.infoItem}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0, marginRight:8}}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
            Transfers arrive in 1–2 business days via ACH
          </div>
          <div style={s.infoItem}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0, marginRight:8}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            Bank details secured by Stripe
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:         {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:       {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:         {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:  {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:         {padding:'0 16px'},
  subtitle:     {fontSize:13, color:'#6E6E73', marginBottom:16, lineHeight:1.5},
  card:         {background:'#fff', borderRadius:20, padding:'4px 0', marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  bankRow:      {display:'flex', alignItems:'center', gap:12, padding:'14px 16px'},
  bankIconWrap: {width:40, height:40, borderRadius:12, background:'#F5F5F7', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0},
  bankIcon:     {fontSize:20, display:'flex', alignItems:'center', justifyContent:'center'},
  bankInfo:     {flex:1, minWidth:0},
  bankName:     {fontSize:14, fontWeight:600, color:'#1D1D1F'},
  bankMeta:     {display:'flex', alignItems:'center', gap:8, marginTop:3},
  bankType:     {fontSize:12, color:'#6E6E73', textTransform:'capitalize'},
  primaryBadge: {background:'#EBF4FF', color:'#007AFF', fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:6},
  bankActions:  {display:'flex', flexDirection:'column', gap:6, flexShrink:0},
  primaryBtn:   {padding:'6px 10px', background:'#F5F5F7', border:'none', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer', whiteSpace:'nowrap'},
  deleteBtn:    {padding:'6px 10px', background:'#FFF0EF', color:'#FF3B30', border:'none', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer'},
  emptyCard:    {background:'#fff', borderRadius:20, padding:32, marginBottom:16, textAlign:'center', boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  emptyIcon:    {fontSize:40, marginBottom:12},
  emptyTitle:   {fontSize:16, fontWeight:600, marginBottom:6},
  emptySub:     {fontSize:13, color:'#6E6E73'},
  msg:          {fontSize:13, fontWeight:500, padding:'10px 14px', borderRadius:10, marginBottom:12, background:'#F5F5F7'},
  formCard:     {background:'#fff', borderRadius:20, padding:20, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  formTitle:    {fontSize:16, fontWeight:600, marginBottom:16},
  input:        {width:'100%', padding:'12px 14px', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, outline:'none', background:'#FAFAFA', marginBottom:10, boxSizing:'border-box'},
  secureNote:   {fontSize:12, color:'#6E6E73', background:'#F5F5F7', borderRadius:8, padding:'8px 12px', marginBottom:14},
  formBtns:     {display:'flex', gap:10},
  cancelBtn:    {flex:1, padding:13, background:'#F5F5F7', border:'none', borderRadius:12, fontSize:14, fontWeight:500, cursor:'pointer'},
  addBtn:       {flex:2, padding:13, background:'#007AFF', color:'#fff', border:'none', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer'},
  addBankBtn:   {width:'100%', padding:16, background:'#fff', border:'1.5px dashed #AEAEB2', borderRadius:16, fontSize:15, fontWeight:500, color:'#6E6E73', cursor:'pointer', marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  infoCard:     {background:'#EBF4FF', borderRadius:16, padding:16, marginBottom:16},
  infoTitle:    {fontSize:13, fontWeight:600, color:'#007AFF', marginBottom:10},
  infoItem:     {fontSize:13, color:'#1D1D1F', marginBottom:8, lineHeight:1.5, display:'flex', alignItems:'center'}
};
